import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Package, Printer, Grid, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import QRCode from 'qrcode';

const API = process.env.REACT_APP_BACKEND_URL;

// Items per page options
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 30, 40];

export default function SharePage() {
  const { shareCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showPerPageMenu, setShowPerPageMenu] = useState(false);
  const observerRef = useRef(null);
  const viewedItems = useRef(new Set());

  useEffect(() => {
    const fetchShare = async () => {
      try {
        const response = await axios.get(`${API}/api/shares/${shareCode}/public`);
        setShareData(response.data);
        
        // Generate QR code
        if (response.data.referral_code) {
          const referralUrl = `${window.location.origin}/pricing?ref=${response.data.referral_code}`;
          const qr = await QRCode.toDataURL(referralUrl, {
            width: 300,
            margin: 2,
            color: { dark: '#10B981', light: '#ffffff' }
          });
          setQrDataUrl(qr);
        }
      } catch (err) {
        console.error('Failed to load share:', err);
        setError('Share not found or has been removed.');
      } finally {
        setLoading(false);
      }
    };

    fetchShare();
  }, [shareCode]);

  // Track item views
  useEffect(() => {
    if (!shareData?.items) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const itemId = entry.target.dataset.itemId;
            if (itemId && !viewedItems.current.has(itemId)) {
              viewedItems.current.add(itemId);
              setTimeout(() => {
                if (viewedItems.current.has(itemId)) {
                  axios.post(`${API}/api/shares/${shareCode}/track-view?item_id=${itemId}`).catch(() => {});
                }
              }, 2000);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    document.querySelectorAll('[data-item-id]').forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [shareData, shareCode]);

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  // Calculate grid columns based on items per page
  const getGridConfig = () => {
    if (itemsPerPage <= 10) {
      return { cols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5', size: 'large' };
    } else if (itemsPerPage <= 20) {
      return { cols: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6', size: 'medium' };
    } else if (itemsPerPage <= 30) {
      return { cols: 'grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7', size: 'small' };
    } else {
      return { cols: 'grid-cols-5 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8', size: 'tiny' };
    }
  };

  // Get item card size classes
  const getItemSizeClasses = (size) => {
    switch(size) {
      case 'large': return { img: 'aspect-square', text: 'text-sm', padding: 'p-3' };
      case 'medium': return { img: 'aspect-square', text: 'text-xs', padding: 'p-2' };
      case 'small': return { img: 'aspect-[4/3]', text: 'text-[10px]', padding: 'p-1.5' };
      case 'tiny': return { img: 'aspect-[4/3]', text: 'text-[9px]', padding: 'p-1' };
      default: return { img: 'aspect-square', text: 'text-xs', padding: 'p-2' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Package className="w-16 h-16 text-muted-foreground" />
        <p className="text-lg font-medium">{error}</p>
        <a href="/" className="text-primary hover:underline">
          Go to FlipValue
        </a>
      </div>
    );
  }

  const { share, items, referral_code } = shareData;
  
  // Determine if branding can be hidden (Pro only)
  const canHideBranding = share.hide_branding === true;
  const canHideQr = share.hide_qr === true;
  
  // Force branding for free/basic
  const showBranding = !canHideBranding;
  const showQr = !canHideQr && qrDataUrl;

  const gridConfig = getGridConfig();
  const sizeClasses = getItemSizeClasses(gridConfig.size);

  // Calculate center position for QR code insertion
  // Insert QR as its own grid item near the center
  const centerIndex = Math.floor(items.length / 2);

  // Build items array with QR code inserted in center
  const buildItemsWithQr = () => {
    if (!showBranding || items.length === 0) {
      return items.map((item, idx) => ({ type: 'item', data: item, index: idx }));
    }

    const result = [];
    for (let i = 0; i < items.length; i++) {
      // Insert QR card at center position
      if (i === centerIndex) {
        result.push({ type: 'qr', index: -1 });
      }
      result.push({ type: 'item', data: items[i], index: i });
    }
    
    // If items array was empty or QR wasn't inserted, add at end
    if (!result.some(r => r.type === 'qr')) {
      result.push({ type: 'qr', index: -1 });
    }
    
    return result;
  };

  const displayItems = buildItemsWithQr();

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-break { page-break-inside: avoid; }
          .print-page { page-break-after: always; }
          @page { margin: 0.5in; }
        }
      `}</style>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card print:bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {showBranding && (
                <div className="flex items-center gap-2">
                  <img src="https://i.imgur.com/hF0H8BY.png" alt="FlipValue" className="h-10 w-auto" />
                  <span className="font-bold text-xl">FlipValue</span>
                </div>
              )}
              {share.title && (
                <span className="text-lg font-medium text-muted-foreground">
                  {showBranding ? `• ${share.title}` : share.title}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Items per page selector */}
              <div className="relative no-print">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowPerPageMenu(!showPerPageMenu)}
                  className="gap-2"
                >
                  <Grid className="w-4 h-4" />
                  {itemsPerPage}/page
                  <ChevronDown className="w-3 h-3" />
                </Button>
                {showPerPageMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                    {ITEMS_PER_PAGE_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        onClick={() => { setItemsPerPage(opt); setShowPerPageMenu(false); }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-secondary transition-colors ${itemsPerPage === opt ? 'bg-primary/10 text-primary font-medium' : ''}`}
                      >
                        {opt} items/page
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handlePrint} variant="outline" className="no-print gap-2">
                <Printer className="w-4 h-4" />
                Print
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-6">
          {/* Custom Message */}
          {share.custom_message && (
            <div className="mb-6 p-4 bg-card rounded-xl border border-border">
              <p className="text-foreground whitespace-pre-wrap">{share.custom_message}</p>
            </div>
          )}

          {/* Items Grid with QR in center */}
          <div className={`grid ${gridConfig.cols} gap-3`}>
            {displayItems.map((entry, idx) => {
              if (entry.type === 'qr') {
                // QR Code Card - takes its own grid space
                return (
                  <div
                    key="qr-card"
                    className="print-break bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/40 dark:to-emerald-900/30 rounded-xl border-2 border-emerald-300 dark:border-emerald-700 flex flex-col items-center justify-center p-2"
                  >
                    {showQr && (
                      <>
                        <img src={qrDataUrl} alt="Scan to visit" className="w-3/4 max-w-[100px] h-auto mb-2" />
                        <div className="flex items-center gap-1.5">
                          <img src="https://i.imgur.com/hF0H8BY.png" alt="FlipValue" className="h-5 w-auto" />
                          <div>
                            <p className="text-emerald-700 dark:text-emerald-300 font-bold leading-tight" style={{ fontSize: sizeClasses.text === 'text-[9px]' ? '10px' : '12px' }}>
                              FlipValue
                            </p>
                            <p className="text-emerald-600 dark:text-emerald-400 leading-tight" style={{ fontSize: sizeClasses.text === 'text-[9px]' ? '8px' : '10px' }}>
                              Scan to start
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              }

              // Regular Item Card
              const item = entry.data;
              return (
                <div
                  key={item.id}
                  data-item-id={item.id}
                  className="print-break bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Item Image */}
                  <div className={`${sizeClasses.img} bg-secondary flex items-center justify-center`}>
                    {item.photo || item.image_url ? (
                      <img
                        src={item.photo || item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>

                  {/* Item Details */}
                  <div className={sizeClasses.padding}>
                    <h3 className={`font-medium ${sizeClasses.text} truncate`}>{item.name}</h3>
                    
                    {item.quantity > 1 && (
                      <p className={`${sizeClasses.text} text-muted-foreground`}>Qty: {item.quantity}</p>
                    )}

                    <div className="mt-1 space-y-0.5">
                      {(item.costPrice !== undefined || item.purchase_price !== undefined) && (
                        <div className={`flex justify-between ${sizeClasses.text}`}>
                          <span className="text-muted-foreground">Cost:</span>
                          <span>{formatCurrency(item.costPrice || item.purchase_price)}</span>
                        </div>
                      )}
                      {(item.currentValue !== undefined || item.estimated_value !== undefined) && (
                        <div className={`flex justify-between ${sizeClasses.text}`}>
                          <span className="text-muted-foreground">Value:</span>
                          <span className="text-primary font-medium">{formatCurrency(item.currentValue || item.estimated_value)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {items.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No items in this collection</p>
            </div>
          )}

          {/* Bottom Branding - for additional visibility */}
          {showBranding && (
            <div className="flex flex-col items-center py-6 mt-6 border-t border-border">
              {showQr && (
                <img src={qrDataUrl} alt="Scan to visit" className="w-28 h-28 mb-3" />
              )}
              <div className="flex items-center gap-2">
                <img src="https://i.imgur.com/hF0H8BY.png" alt="FlipValue" className="h-8 w-auto" />
                <span className="text-lg font-bold">FlipValue</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Know Your Worth Before You Sell
              </p>
            </div>
          )}

          {/* Footer */}
          <footer className="text-center py-4">
            <p className="text-sm text-muted-foreground">
              Powered by{' '}
              <a 
                href={`/${referral_code ? `pricing?ref=${referral_code}` : ''}`}
                className="text-primary hover:underline font-medium"
              >
                FlipValue
              </a>
            </p>
          </footer>
        </main>
      </div>
    </>
  );
}
